import React, { useState, useRef } from "react";
import Layout from "../../components/common/Layout";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import { useAuth } from "../../context/AuthContext";
import { useDepartments } from "../../hooks/useFirestore";
import { db } from "../../firebase/config";
import { collection, addDoc, serverTimestamp, getDocs, query, where, orderBy, doc, getDoc } from "firebase/firestore";

/* ── CONFIGURATION ───────────────────────────────────────────────────────── */
// 1. Deploy your Google Apps Script and paste the resulting Web App URL here
const WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbySZ9XJQQ26ZCDNm4FRqdUDDLEhb0UeXpuugHssmqGVMeH1HACMm7iYQsTk9YW8Qw3t/exec";
// 2. Secret Token for endpoint security
const API_TOKEN = "EMS_SECURE_TOKEN_2026_X9QZ";
/* ───────────────────────────────────────────────────────────────────────── */

/* ── DESIGN TOKENS — enterprise dashboard system ───────────────────────── */
const PRIMARY = "#164231";        // Deep Forest Green — brand/primary
const PRIMARY_HOVER = "#0F3024";  // Darker shade for button hover
const PRIMARY_LIGHT = "#EAF2EE";  // Light tint — highlighted cells, badges, soft backgrounds
const PRIMARY_SOFT = "#F2F7F4";   // Even softer tint — hover backgrounds on outline elements
const TEXT = "#1E293B";           // Dark slate — body text
const MUTED = "#64748B";          // Muted gray — subtitles, hints, captions
const BORDER = "#D9DEE3";         // Light border — enclosed inputs & cards
const BG = "transparent";         // Page background — set to transparent to blend with your app's background
const WHITE = "#FFFFFF";
const ERROR = "#DC2626";
const SHADOW = "0 4px 12px rgba(0,0,0,0.05)";
// Each expense section (Transportation, Accommodation, Meals, Other) is capped at
// this amount. Also enforced per-field so a single row can never exceed it, which
// keeps subtotals from ever overflowing into scientific notation (e.g. "1e+41").
const MAX_SECTION_TOTAL = 100000;
// Own Vehicle mileage reimbursement rate (₹ per km).
const KM_RATE = 4;

const styles = {
  page: { background: BG, fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif", padding: "24px" },
  formWrap: { maxWidth: 1000, width: "100%", margin: "0 auto" },

  headerCard: { background: WHITE, borderRadius: 12, marginBottom: 16, boxShadow: SHADOW, padding: "24px 28px" },
  headerTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" },
  formTitle: { fontSize: 24, fontWeight: 700, color: PRIMARY, margin: "0 0 6px", letterSpacing: "-0.01em" },
  formDesc: { fontSize: 14, color: MUTED, lineHeight: 1.6, margin: 0, maxWidth: 640 },
  requiredNote: { fontSize: 12, color: MUTED, marginTop: 10 },
  ghostBtn: { background: "none", border: "none", color: MUTED, fontSize: 13, fontWeight: 500, cursor: "pointer", padding: "6px 10px", borderRadius: 6, whiteSpace: "nowrap" },

  // Stepper
  stepperCard: { background: WHITE, borderRadius: 12, marginBottom: 16, boxShadow: SHADOW, padding: "18px 24px" },
  stepProgressText: { fontSize: 12, color: MUTED, marginBottom: 12, fontWeight: 500 },
  stepperWrap: { display: "flex", alignItems: "flex-start", overflowX: "auto", paddingBottom: 2 },
  stepNode: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 64 },
  stepCircle: { width: 28, height: 28, borderRadius: "50%", border: `2px solid ${BORDER}`, background: WHITE, color: MUTED, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1, flexShrink: 0 },
  stepCircleDone: { background: PRIMARY, borderColor: PRIMARY, color: WHITE },
  stepCircleActive: { background: WHITE, borderColor: PRIMARY, color: PRIMARY, boxShadow: `0 0 0 3px ${PRIMARY_LIGHT}` },
  stepLabel: { fontSize: 11, color: MUTED, fontWeight: 500, whiteSpace: "nowrap" },
  stepLabelActive: { color: PRIMARY, fontWeight: 700 },
  stepConnector: { flex: 1, height: 2, background: BORDER, marginTop: 13, minWidth: 16 },
  stepConnectorDone: { background: PRIMARY },

  sectionCard: { background: WHITE, borderRadius: 12, marginBottom: 16, boxShadow: SHADOW, overflow: "hidden" },
  sectionHeader: { padding: "20px 24px 14px", borderBottom: `1px solid ${BORDER}` },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: PRIMARY, margin: "0 0 4px" },
  sectionSub: { fontSize: 13, color: MUTED, margin: 0 },
  sectionBody: { padding: "20px 24px 24px" },

  fieldGroup: { marginTop: 18 },
  label: { fontSize: 13, color: TEXT, fontWeight: 500, display: "block", marginBottom: 6 },
  required: { color: ERROR, marginLeft: 2 },

  input: { width: "100%", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "9px 12px", fontSize: 14, color: TEXT, background: WHITE, outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 },
  select: { width: "100%", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "9px 12px", fontSize: 14, color: TEXT, background: WHITE, outline: "none", boxSizing: "border-box", cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s" },

  tableWrap: { overflowX: "auto", marginTop: 16 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { background: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 600, padding: "10px 8px", textAlign: "left", borderBottom: `2px solid ${PRIMARY}`, whiteSpace: "nowrap" },
  td: { padding: "8px 6px", borderBottom: `1px solid ${BORDER}`, verticalAlign: "middle" },
  tInput: { width: "100%", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 8px", fontSize: 13, color: TEXT, background: WHITE, outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s" },
  addRowBtn: { marginTop: 10, background: "none", border: `1px dashed ${PRIMARY}`, color: PRIMARY, borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  removeBtn: { background: "none", border: "none", color: ERROR, cursor: "pointer", fontSize: 16, padding: "0 4px", lineHeight: 1 },
  subTotal: { background: PRIMARY_LIGHT, fontWeight: 600, color: PRIMARY, padding: "8px 8px", textAlign: "right", fontSize: 13, borderTop: `2px solid ${PRIMARY}` },

  cardsWrap: { display: "flex", flexDirection: "column", gap: 12, marginTop: 16 },
  card: { border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 16px", background: WHITE },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${BORDER}` },
  cardHeaderTitle: { fontSize: 13, fontWeight: 700, color: PRIMARY },
  cardField: { marginBottom: 10 },
  cardFieldLabel: { fontSize: 11, color: MUTED, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.02em" },
  cardFooterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${BORDER}` },
  cardSubTotal: { background: PRIMARY_LIGHT, borderRadius: 8, padding: "10px 14px", fontWeight: 700, color: PRIMARY, fontSize: 14, display: "flex", justifyContent: "space-between", marginTop: 4 },

  uploadZone: { border: `2px dashed ${PRIMARY}`, borderRadius: 10, padding: "16px", marginTop: 16, background: PRIMARY_LIGHT, textAlign: "center", cursor: "pointer", transition: "background 0.2s" },
  uploadZoneHover: { background: "#DFEAE4" },
  uploadBtn: { background: PRIMARY, color: WHITE, border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 500, cursor: "pointer", marginTop: 8 },
  fileList: { listStyle: "none", padding: 0, margin: "10px 0 0", textAlign: "left" },
  fileItem: { display: "flex", alignItems: "center", justifyContent: "space-between", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 10px", marginBottom: 6, fontSize: 13, color: TEXT },
  fileRemove: { background: "none", border: "none", color: ERROR, cursor: "pointer", fontSize: 15, marginLeft: 8 },

  policyBox: { background: PRIMARY_LIGHT, borderRadius: 8, padding: "16px 18px", marginTop: 4 },
  policyItem: { marginBottom: 12 },
  policyTitle: { fontSize: 13, fontWeight: 600, color: PRIMARY, marginBottom: 2 },
  policyText: { fontSize: 13, color: MUTED, lineHeight: 1.5 },

  summaryTable: { width: "100%", borderCollapse: "collapse", fontSize: 14, marginTop: 8 },
  sTh: { background: PRIMARY, color: WHITE, padding: "10px 12px", fontWeight: 600, textAlign: "left" },
  sTr: { borderBottom: `1px solid ${BORDER}` },
  sTd: { padding: "10px 12px", color: TEXT },
  sTdRight: { padding: "10px 12px", color: TEXT, textAlign: "right" },
  sTrTotal: { background: PRIMARY_LIGHT, fontWeight: 700, borderTop: `2px solid ${PRIMARY}` },

  navRow: { marginTop: 28, paddingTop: 20, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" },
  navSpacer: { flex: 1 },

  primaryBtn: { background: PRIMARY, color: WHITE, border: "none", borderRadius: 8, padding: "12px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer", transition: "background 0.15s" },
  secondaryBtn: { background: WHITE, color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 20px", fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "background 0.15s, border-color 0.15s" },

  successCard: { background: WHITE, borderRadius: 12, boxShadow: SHADOW, padding: "48px 32px", textAlign: "center" },
  successIcon: { width: 56, height: 56, borderRadius: "50%", background: PRIMARY_LIGHT, color: PRIMARY, fontSize: 28, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" },
  successTitle: { fontSize: 19, fontWeight: 700, color: PRIMARY, margin: "0 0 8px" },
  successText: { fontSize: 14, color: MUTED, lineHeight: 1.6, maxWidth: 420, margin: "0 auto 24px" },

  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 },
};

function DesignSystemStyles() {
  return (
    <style>{`
      .ent-input:focus {
        border-color: ${PRIMARY} !important;
        box-shadow: 0 0 0 3px ${PRIMARY_LIGHT} !important;
      }
      .ent-btn-primary:hover { background: ${PRIMARY_HOVER} !important; }
      .ent-btn-secondary:hover { background: ${PRIMARY_SOFT} !important; border-color: ${PRIMARY} !important; }
      .ent-btn-ghost:hover { background: ${PRIMARY_SOFT} !important; }
      .ent-step-btn:disabled { cursor: default !important; }
      #declarationAgree:focus-visible {
        outline: 2px solid ${PRIMARY} !important;
        outline-offset: 2px !important;
      }
      @media (max-width: 640px) {
        .ent-step-label { display: none !important; }
      }
      .exp-table-view { display: block; }
      .exp-cards-view { display: none; }
      @media (max-width: 768px) {
        .exp-table-view { display: none; }
        .exp-cards-view { display: block; }
      }
    `}</style>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>
        {label}{required && <span style={styles.required}>*</span>}
        {hint && <span style={{ fontSize: 12, color: MUTED, fontWeight: 400, marginLeft: 6 }}>({hint})</span>}
      </label>
      {children}
    </div>
  );
}

function SectionLimitNote({ total, limit }) {
  const overLimit = total > limit;
  return (
    <p style={{
      fontSize: 12,
      marginTop: 8,
      color: overLimit ? ERROR : MUTED,
      fontWeight: overLimit ? 700 : 400,
    }}>
      {overLimit
        ? `⚠️ Section total ₹${total.toFixed(2)} exceeds the ₹${limit.toLocaleString("en-IN")} limit for this section. Please reduce the amounts before proceeding.`
        : `Section limit: ₹${limit.toLocaleString("en-IN")} maximum.`}
    </p>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ""}
      className="ent-input" style={styles.input} />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select className="ent-input" style={styles.select} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function RowFileUpload({ files = [], onFilesChange }) {
  const fileInputRef = useRef(null);
  const MAX_FILES = 5;

  const handleRowFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    // Check file count limit first
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      alert(`⚠️ Bill Limit Reached!\n\nYou can attach a maximum of ${MAX_FILES} bills per row.\n\nPlease remove an existing bill before adding a new one.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    const oversized = [];
    const valid = [];

    selected.forEach(f => {
      if (f.size > MAX_SIZE) {
        oversized.push(f);
      } else {
        valid.push(f);
      }
    });

    if (oversized.length > 0) {
      const names = oversized.map(f => `• ${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB)`).join("\n");
      alert(`⚠️ File Size Exceeded!\n\nThe following file(s) exceed the 10 MB limit:\n${names}\n\nPlease attach files smaller than 10 MB.`);
    }

    if (valid.length > 0) {
      const toAdd = valid.slice(0, remaining); // Clamp to remaining slots
      if (valid.length > remaining) {
        alert(`⚠️ Bill Limit!\n\nOnly ${remaining} more bill(s) can be added (max ${MAX_FILES} per row).\n\nThe first ${remaining} file(s) were attached.`);
      }
      const formatted = toAdd.map(f => ({
        name: f.name,
        size: f.size,
        id: Math.random().toString(36).slice(2),
        file: f
      }));
      onFilesChange([...files, ...formatted]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (id) => {
    onFilesChange(files.filter(f => f.id !== id));
  };

  const fmtSize = (bytes) => bytes < 1024 ? `${bytes} B` : bytes < 1048576 ? `${(bytes/1024).toFixed(0)}KB` : `${(bytes/1048576).toFixed(1)}MB`;

  const atLimit = files.length >= MAX_FILES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        style={{ display: "none" }}
        onChange={handleRowFiles}
        disabled={atLimit}
      />
      <button
        type="button"
        className="ent-btn-ghost"
        style={{
          fontSize: 11,
          padding: "4px 8px",
          borderRadius: 4,
          border: `1px dashed ${atLimit ? "#9ca3af" : files.length > 0 ? "#10b981" : PRIMARY}`,
          color: atLimit ? "#9ca3af" : files.length > 0 ? "#047857" : PRIMARY,
          background: atLimit ? "#f3f4f6" : files.length > 0 ? "#ecfdf5" : "transparent",
          cursor: atLimit ? "not-allowed" : "pointer",
          fontWeight: 600,
          whiteSpace: "nowrap"
        }}
        onClick={() => { if (!atLimit) fileInputRef.current?.click(); }}
        title={atLimit ? `Maximum ${MAX_FILES} bills per row reached` : "Attach bill or proof"}
      >
        {files.length > 0
          ? `📎 ${files.length}/${MAX_FILES} Bill(s) Attached`
          : "📎 Attach Bill"}
      </button>

      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 160 }}>
          {files.map(f => (
            <div
              key={f.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 10,
                background: "#f3f4f6",
                padding: "2px 6px",
                borderRadius: 4,
                color: "#374151"
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }} title={f.name}>
                📄 {f.name} ({fmtSize(f.size)})
              </span>
              <button
                type="button"
                onClick={() => removeFile(f.id)}
                style={{ background: "none", border: "none", color: "#ef4444", fontWeight: "bold", cursor: "pointer", padding: "0 2px", fontSize: 12 }}
                title="Remove bill"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function emptyRow(cols) {
  const row = Object.fromEntries(cols.map(c => [c.key, ""]));
  row.files = [];
  return row;
}

function ExpenseTable({ columns, rows, onChange }) {
  const subtotal = rows.reduce((sum, r) => {
    const v = parseFloat(r.amount || 0);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  const updateRow = (i, key, val) => onChange(rows.map((r, idx) => {
    if (idx !== i) return r;
    const newRow = { ...r, [key]: val };
    if (newRow.mode === "Own Vehicle" && key === "km") {
      // Auto calculate ₹4 per Km, capped at the per-section limit.
      // On overflow, clear the amount rather than clamp it, so the user
      // re-enters a valid Km value instead of getting stuck at the cap.
      const rawKmAmount = (parseFloat(val) || 0) * KM_RATE;
      if (rawKmAmount > MAX_SECTION_TOTAL) {
        alert(`⚠️ Amount Limit Exceeded!\n\nThe calculated mileage amount (₹${rawKmAmount.toFixed(2)}) exceeds the ₹${MAX_SECTION_TOTAL.toLocaleString("en-IN")} limit for this section.\n\nPlease re-enter the Km for this row.`);
        newRow.amount = '';
      } else {
        newRow.amount = rawKmAmount.toFixed(2);
      }
    }
    // Auto-calculate nights from check-in / check-out dates
    if ((key === "checkin" || key === "checkout") && newRow.checkin && newRow.checkout) {
      const d1 = new Date(newRow.checkin);
      const d2 = new Date(newRow.checkout);
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
      newRow.nights = diff > 0 ? String(diff) : newRow.nights;
    }
    // Auto-calculate total amount for accommodation: Nights * Per Night Rate,
    // capped at the per-section limit
    if (key === "checkin" || key === "checkout" || key === "nights" || key === "ratePerNight") {
      if (newRow.ratePerNight !== undefined && newRow.ratePerNight !== "") {
        const n = parseInt(newRow.nights) || 0;
        const r = parseFloat(newRow.ratePerNight) || 0;
        const rawAccomAmount = n * r;
        if (rawAccomAmount > MAX_SECTION_TOTAL) {
          alert(`⚠️ Amount Limit Exceeded!\n\nThe calculated amount (Nights × Rate = ₹${rawAccomAmount.toFixed(2)}) exceeds the ₹${MAX_SECTION_TOTAL.toLocaleString("en-IN")} limit for this section.\n\nPlease re-enter the Nights / Per Night rate for this row.`);
          newRow.amount = '';
        } else {
          newRow.amount = rawAccomAmount.toFixed(2);
        }
      }
    }
    return newRow;
  }));
  const addRow = () => onChange([...rows, emptyRow(columns)]);
  const removeRow = i => onChange(rows.filter((_, idx) => idx !== i));

  // Date pickers are restricted to the current reimbursement cycle. Two cycle
  // variants are supported per-column via `c.cycle`:
  //   • "25" — 25th of previous month → 25th of current month
  //            (Accommodation, Other Incidentals)
  //   • "24" — 24th of previous month → 24th of current month
  //            (Transportation, Food & Meals)
  // For a field with a `maxRef` (e.g. Check-in referencing Check-out), the
  // effective max is whichever is earlier: the paired field's value or the
  // cycle end. `capEnd` controls whether the cycle-end cap applies at all.
  const _now = new Date();
  const _year = _now.getFullYear();
  const _month = _now.getMonth(); // 0-indexed
  const _cycleStartDate25 = new Date(_year, _month - 1, 25);
  const _cycleEndDate25 = new Date(_year, _month, 25);
  const _cycleStartDate24 = new Date(_year, _month - 1, 24);
  const _cycleEndDate24 = new Date(_year, _month, 24);
  const firstOfYear25 = _cycleStartDate25.toISOString().split('T')[0];
  const cycleEnd25 = _cycleEndDate25.toISOString().split('T')[0];
  const firstOfYear24 = _cycleStartDate24.toISOString().split('T')[0];
  const cycleEnd24 = _cycleEndDate24.toISOString().split('T')[0];

  const CYCLE_BOUNDS = {
    "24": { start: firstOfYear24, end: cycleEnd24 },
    "25": { start: firstOfYear25, end: cycleEnd25 },
  };
  const getCycleBounds = (c) => CYCLE_BOUNDS[c.cycle || "25"];

  // ISO "YYYY-MM-DD" strings sort correctly across month/year boundaries,
  // so plain string comparison is safe here.
  const earlierISODate = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    return a < b ? a : b;
  };

  const fieldIsDisabled = (c, row) => {
    if (c.key === "km" && row.mode !== "Own Vehicle") return true;
    return false;
  };

  const renderField = (c, row, i) => {
    if (c.type === "select") {
      return (
        <select className="ent-input" style={styles.tInput} value={row[c.key]} onChange={e => updateRow(i, c.key, e.target.value)}>
          <option value="">—</option>
          {c.options.map(o => <option key={o}>{o}</option>)}
        </select>
      );
    }
    if (c.type === "number") {
      return (
        <input type="number" className="ent-input" style={styles.tInput} value={row[c.key]}
          onChange={e => {
            const inputEl = e.target;
            let val = inputEl.value.replace(/-/g, '');
            if (c.integer) {
              // Integers only: strip any decimal part
              val = val.split('.')[0];
            } else {
              // Restrict to max 2 decimal places (applies to Amount too, so it always
              // reads as a proper decimal value like 250.50 rather than 250.5123)
              if (val.includes('.')) {
                const [int, dec] = val.split('.');
                if (dec && dec.length > 2) val = `${int}.${dec.slice(0, 2)}`;
              }
            }
            // Amount can never exceed the per-section cap. Rather than silently
            // clamping to the cap (which let further typing keep re-triggering
            // the alert, making it look like the popup wouldn't close), clear
            // the field on overflow so the user has to re-enter the amount.
            if (c.key === "amount" && val !== '') {
              const num = parseFloat(val);
              if (!isNaN(num) && num > MAX_SECTION_TOTAL) {
                alert(`⚠️ Amount Limit Exceeded!\n\nA single row's amount cannot exceed ₹${MAX_SECTION_TOTAL.toLocaleString("en-IN")}.\n\nPlease re-enter the amount for this row.`);
                val = '';
              }
            }
            updateRow(i, c.key, val);
            // The browser's alert() dialog steals focus away from the input
            // whenever an over-limit warning fires — either right here for a
            // directly-typed Amount, or inside updateRow() for an auto-calculated
            // Amount (Own Vehicle Km × rate, or Accommodation Nights × rate).
            // Either way the field looks "stuck"/unresponsive even though it's
            // just idle, so always restore focus once the re-render settles.
            requestAnimationFrame(() => { if (inputEl) inputEl.focus(); });
          }}
          onBlur={e => {
            const inputEl = e.target;
            if (!c.integer && inputEl.value !== '') {
              // Auto-format to 2 decimal places on blur (e.g. "10" → "10.00"),
              // including Amount, so it's always shown in decimal format.
              let num = parseFloat(inputEl.value);
              if (!isNaN(num)) {
                if (c.key === "amount" && num > MAX_SECTION_TOTAL) {
                  // Safety net — normally onChange already clears an over-limit
                  // value before it ever reaches blur. Restore focus afterward
                  // since alert() steals it away.
                  alert(`⚠️ Amount Limit Exceeded!\n\nA single row's amount cannot exceed ₹${MAX_SECTION_TOTAL.toLocaleString("en-IN")}.\n\nPlease re-enter the amount for this row.`);
                  updateRow(i, c.key, '');
                  requestAnimationFrame(() => { if (inputEl) inputEl.focus(); });
                  return;
                }
                updateRow(i, c.key, num.toFixed(2));
              }
            }
          }}
          onKeyDown={e => {
            if (e.key === '-' || e.key === 'e') e.preventDefault();
            if (c.integer && e.key === '.') e.preventDefault();
          }}
          placeholder={c.placeholder || ""} min="0" max={c.key === "amount" ? MAX_SECTION_TOTAL : undefined}
          step={c.key === "amount" ? "any" : (c.integer ? "1" : "0.01")} />
      );
    }
    if (c.type === "date") {
      const bounds = getCycleBounds(c);
      const refCap = c.maxRef && row[c.maxRef] ? row[c.maxRef] : undefined;
      const maxVal = c.capEnd ? earlierISODate(refCap, bounds.end) : refCap;
      const minVal = (c.minRef && row[c.minRef] && row[c.minRef] > bounds.start) ? row[c.minRef] : bounds.start;
      return (
        <input type="date" className="ent-input" style={styles.tInput} value={row[c.key]}
          max={maxVal} min={minVal}
          onChange={e => updateRow(i, c.key, e.target.value)} placeholder={c.placeholder || ""} />
      );
    }
    return (
      <input type={c.type || "text"} className="ent-input" style={styles.tInput} value={row[c.key]}
        onChange={e => {
          let val = e.target.value;
          if (c.key === "route" || c.key === "desc" || c.key === "location" || c.key === "remarks") {
            val = val.replace(/[^a-zA-Z0-9\s.,\-()]/g, '');
          }
          updateRow(i, c.key, val);
        }} placeholder={c.placeholder || ""} />
    );
  };

  return (
    <>
      {/* DESKTOP: standard table, horizontally scrollable as a fallback */}
      <div className="exp-table-view" style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map(c => <th key={c.key} style={styles.th}>{c.label}</th>)}
              <th style={styles.th}>Attach Bill / Proof</th>
              <th style={styles.th}>Remove</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map(c => (
                  <td key={c.key} style={styles.td}>
                    {fieldIsDisabled(c, row) ? null : renderField(c, row, i)}
                  </td>
                ))}
                <td style={styles.td}>
                  <RowFileUpload files={row.files || []} onFilesChange={newFiles => updateRow(i, "files", newFiles)} />
                </td>
                <td style={styles.td}>
                  <button style={styles.removeBtn} onClick={() => removeRow(i)} title="Remove row">×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={columns.length - 1} style={styles.subTotal}>Sub-Total</td>
              <td style={{ ...styles.subTotal, textAlign: "left" }}>₹ {subtotal.toFixed(2)}</td>
              <td style={styles.subTotal}></td>
              <td style={styles.subTotal}></td>
            </tr>
          </tfoot>
        </table>
        <button style={styles.addRowBtn} onClick={addRow}>+ Add row</button>
      </div>

      {/* MOBILE: stacked cards, no horizontal scrolling required */}
      <div className="exp-cards-view">
        <div style={styles.cardsWrap}>
          {rows.map((row, i) => (
            <div key={i} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardHeaderTitle}>Row {i + 1}</span>
                <button style={styles.removeBtn} onClick={() => removeRow(i)} title="Remove row">× Remove</button>
              </div>
              {columns.map(c => (
                fieldIsDisabled(c, row) ? null : (
                  <div key={c.key} style={styles.cardField}>
                    <label style={styles.cardFieldLabel}>{c.label}</label>
                    {renderField(c, row, i)}
                  </div>
                )
              ))}
              <div style={styles.cardField}>
                <label style={styles.cardFieldLabel}>Attach Bill / Proof</label>
                <RowFileUpload files={row.files || []} onFilesChange={newFiles => updateRow(i, "files", newFiles)} />
              </div>
            </div>
          ))}
        </div>
        <div style={styles.cardSubTotal}>
          <span>Sub-Total</span>
          <span>₹ {subtotal.toFixed(2)}</span>
        </div>
        <button style={styles.addRowBtn} onClick={addRow}>+ Add row</button>
      </div>
    </>
  );
}

function Stepper({ steps, currentStep, maxStepReached, onStepClick }) {
  return (
    <div>
      <p style={styles.stepProgressText}>Step {currentStep + 1} of {steps.length}: {steps[currentStep].label}</p>
      <div style={styles.stepperWrap}>
        {steps.map((step, i) => {
          const done = i < currentStep;
          const active = i === currentStep;
          const clickable = i <= maxStepReached || maxStepReached >= 3;
          return (
            <div key={step.label} style={{ display: "flex", alignItems: "flex-start", flex: i < steps.length - 1 ? 1 : "0 0 auto" }}>
              <div style={styles.stepNode}>
                <button
                  type="button"
                  className="ent-step-btn"
                  onClick={() => clickable && onStepClick(i)}
                  disabled={!clickable}
                  aria-current={active ? "step" : undefined}
                  title={step.label}
                  style={{
                    ...styles.stepCircle,
                    ...(done ? styles.stepCircleDone : {}),
                    ...(active ? styles.stepCircleActive : {}),
                    cursor: clickable ? "pointer" : "default",
                  }}
                >
                  {done ? "✓" : i + 1}
                </button>
                <span className="ent-step-label" style={{ ...styles.stepLabel, ...(active ? styles.stepLabelActive : {}) }}>{step.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ ...styles.stepConnector, ...(done ? styles.stepConnectorDone : {}) }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const transportCols = [
  { key: "date", label: "Date", type: "date", cycle: "25", capEnd: true },
  { key: "mode", label: "Mode", type: "select", options: ["Flight", "Train", "Bus", "Cab/Auto", "Own Vehicle", "Metro", "Other"] },
  { key: "route", label: "Route (From → To)", placeholder: "e.g. Mumbai → Delhi" },
  { key: "km", label: "Km", type: "number", placeholder: "e.g. 50", integer: true },
  { key: "amount", label: "Amount (₹)", type: "number", placeholder: "0.00" },
];
const accomCols = [
  { key: "checkin", label: "Check-in", type: "date", cycle: "25", maxRef: "checkout", capEnd: true },
  { key: "checkout", label: "Check-out", type: "date", cycle: "25", minRef: "checkin", capEnd: true },
  { key: "hotel", label: "Hotel / Property", placeholder: "Hotel name" },
  { key: "location", label: "Location / City", placeholder: "e.g. Mumbai" },
  { key: "nights", label: "Nights", type: "number", placeholder: "1", integer: true },
  { key: "ratePerNight", label: "Per Night (₹)", type: "number", placeholder: "0.00" },
  { key: "amount", label: "Amount (₹)", type: "number", placeholder: "0.00" },
  { key: "remarks", label: "Remarks / Business Justification", placeholder: "e.g. Client meeting, site visit" },
];
const mealCols = [
  { key: "date", label: "Date", type: "date", cycle: "25", capEnd: true },
  { key: "desc", label: "Description / Vendor", placeholder: "Restaurant or meal type" },
  { key: "amount", label: "Amount (₹)", type: "number", placeholder: "0.00" },
];
const otherCols = [
  { key: "date", label: "Date", type: "date", cycle: "25", capEnd: true },
  { key: "desc", label: "Description", placeholder: "e.g. Visa fee, toll, parking" },
  { key: "amount", label: "Amount (₹)", type: "number", placeholder: "0.00" },
];

const policies = [
  { title: "Submission Deadline", text: "Submit your reimbursement claim by the 25th of every month as per the calendar." },
  { title: "Travel Booking", text: "Book via the approved corporate travel portal. Economy class is standard; Business class needs pre-approval for flights over 6 hours." },
  { title: "Accommodation Limits", text: "Hotel limits are grade-based (see Travel Policy Annexure A). Personal stays or relative homes are not reimbursable." },
  { title: "Meal Allowance", text: "Reimbursed at actuals up to the daily limit per grade. Business entertainment must list all attendee names." },
  { title: "Original Receipts", text: "Every receipt must show vendor name, date, itemised amount, and GST details." },
  { title: "Advance Settlement", text: "Travel advances must be settled within 7 days of return. Unspent amounts must be returned before new advances are issued." },
  { title: "Non-Reimbursable Items", text: "Alcohol, personal entertainment, fines, personal clothing, minibar charges, personal travel insurance, and unapproved upgrades." },
];

const STEPS = [
  { label: "Policy", title: "Section 1 — Reimbursement Policy Reference", sub: "Key rules — read before proceeding" },
  { label: "Declaration", title: "Section 2 — Declaration", sub: "You must accept this declaration to proceed" },
  { label: "Employee", title: "Section 3 — Employee Information", sub: "Personal and department details of the claimant" },
  { label: "Transport", title: "Section 4 — Transportation Expenses", sub: "Flight, train, bus, cab, own vehicle — add a row for each journey leg" },
  { label: "Accommodation", title: "Section 5 — Accommodation Expenses", sub: "Hotel, guesthouse, or serviced apartment charges" },
{ label: "Meals", title: "Section 6 — Food & Meal Expenses", sub: "Daily meals, client entertaining, and per-diem allowances" },
  { label: "Other", title: "Section 7 — Other Incidental Expenses", sub: "Visa fees, forex, laundry (extended trips), tolls, parking, courier, etc." },
  { label: "Summary", title: "Section 8 — Expense Summary", sub: "Auto-calculated from the tables above" },
];

const TravelReimbursementHistory = ({ claims, loading, error, onRefresh }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = claims.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter && (statusFilter === "PENDING_FINANCE" ? c.status !== "PENDING" : true)) {
      if (statusFilter === "PENDING" && c.status === "PENDING_FINANCE") return true;
      if (c.status !== statusFilter) return false;
    }
    if (fromDate && c.date < fromDate) return false;
    if (toDate && c.date > toDate) return false;
    return true;
  });

  if (loading) return <div className="card-loading">Loading claims...</div>;

  if (error) return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
      <p style={{ color: ERROR, fontWeight: 600, marginBottom: 8 }}>{error}</p>
      <button
        onClick={onRefresh}
        style={{ background: PRIMARY, color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
      >
        🔄 Retry
      </button>
    </div>
  );

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Claims</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: MUTED }}>From:</label>
          <input type="date" value={fromDate} max={toDate || new Date().toISOString().split("T")[0]} onChange={e => setFromDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: MUTED }}>To:</label>
          <input type="date" value={toDate} min={fromDate} max={new Date().toISOString().split("T")[0]} onChange={e => setToDate(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 13 }} />
        </div>
        {(fromDate || toDate) && (
          <button className="btn-ghost-sm" onClick={() => { setFromDate(""); setToDate(""); }} style={{ fontSize: 13 }}>Clear Dates</button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="card-empty">No claims found.</div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Advance</th>
                <th>Payable</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td>{c.date}</td>
                  <td>₹{parseFloat(c.grandTotal + (c.advance || 0)).toFixed(2)}</td>
                  <td>₹{parseFloat(c.advance || 0).toFixed(2)}</td>
                  <td><strong>₹{parseFloat(c.grandTotal || 0).toFixed(2)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function TravelReimbursementNew() {
  const { userProfile, currentUser } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  const today = new Date();
  const isSubmissionAllowed = today.getDate() <= 25;

  const { departments } = useDepartments();
  const activeDepts = departments && departments.length > 0
    ? departments.filter((d) => d.active).map((d) => d.name)
    : [];

  const [submitted, setSubmitted] = useState(false);
  const [currentStepState, setCurrentStepState] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);

  const setCurrentStep = (val) => {
    setCurrentStepState(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      setMaxStepReached(m => Math.max(m, next));
      return next;
    });
  };
  const currentStep = currentStepState;

  const u = (setter, field, replaceRegex) => (val) => {
    if (replaceRegex && typeof val === "string") val = val.replace(replaceRegex, '');
    setter(prev => ({ ...prev, [field]: val }));
  };

  const [emp, setEmp] = useState({ name: "", dept: "", email: "", phone: "" });

  // These fields are read-only in Section 3 (the person can't type into
  // them), so there's no reason to keep whatever value was set first —
  // always sync from the latest AuthContext profile. This makes sure a
  // profile edit (e.g. via Admin → Edit Employee, or the person's own
  // My Profile page) is reflected here instead of getting stuck on
  // whatever was loaded when this form first mounted in the session.
  React.useEffect(() => {
    if (userProfile) {
      setEmp(prev => ({
        ...prev,
        name: userProfile.displayName || userProfile.name || "",
        email: userProfile.email || "",
        dept: userProfile.department || "",
        // "phone" is set at onboarding time (see AdminEmployees.jsx: formData.phone → users/{uid}.phone)
        // userProfile may not always carry phone even when Firestore does, so keep
        // whatever we already have as a fallback — the effect below is the real
        // source of truth for this field and runs right after.
        phone: userProfile.phone || prev.phone || "",
      }));
    }
  }, [userProfile]);

  // Belt-and-suspenders: userProfile from AuthContext can be a stale/partial
  // cache, so also pull the Mobile Number straight from the same users/{uid}
  // document the Employee Master (onboarding wizard) writes to. This always
  // overwrites the current value (rather than only filling a blank) so a
  // later profile edit is actually reflected instead of staying stuck on
  // whatever was fetched first in this session.
  React.useEffect(() => {
    if (!currentUser?.uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          if (data.phone) {
            setEmp(prev => ({ ...prev, phone: data.phone }));
          }
        }
      } catch (e) {
        console.error("Could not fetch employee phone from users/{uid}:", e);
      }
    })();
  }, [currentUser]);

  const [transport, setTransport] = useState([emptyRow(transportCols), emptyRow(transportCols)]);
  const [accom, setAccom] = useState([emptyRow(accomCols)]);
  const [meals, setMeals] = useState([emptyRow(mealCols), emptyRow(mealCols)]);
  const [other, setOther] = useState([emptyRow(otherCols)]);
  const [transportFiles, setTransportFiles] = useState([]);
  const [accomFiles, setAccomFiles] = useState([]);
  const [mealFiles, setMealFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [advance, setAdvance] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep > 0 && !submitted) {
        e.preventDefault();
        e.returnValue = "You have unsaved reimbursement data. Are you sure you want to refresh and lose your progress?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [currentStep, submitted]);

  // On the Declaration step, pressing Space toggles the agreement checkbox —
  // regardless of what currently has focus (e.g. the Next/Back buttons),
  // as long as the user isn't actively typing in a text field.
  React.useEffect(() => {
    if (currentStep !== 1) return;

    const isTypingField = (el) => {
      const tag = el?.tagName;
      return tag === "INPUT" && el.id !== "declarationAgree"
        ? true
        : tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable;
    };

    const handleKeyDown = (e) => {
      if (e.code !== "Space" && e.key !== " ") return;
      const active = document.activeElement;
      if (isTypingField(active)) return; // let normal typing/space work as usual

      // Stop this keypress dead in its tracks so it can't also trigger
      // a focused button's click (which fires on keyup for Space).
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      // If a button/link currently holds focus, move focus away from it
      // so a later stray keyup can't re-trigger its click.
      if (active && active.tagName === "BUTTON" && active.id !== "declarationAgree") {
        active.blur();
      }

      setAgreed(a => !a);
    };

    const handleKeyUp = (e) => {
      if (e.code !== "Space" && e.key !== " ") return;
      const active = document.activeElement;
      if (isTypingField(active)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
    };

    // Capture phase so we intercept before the focused element's own
    // handlers (e.g. a button's built-in space-to-click behavior) run.
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
    };
  }, [currentStep]);

  const [myClaims, setMyClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(true);
  const [claimsFetchError, setClaimsFetchError] = useState("");
  const [activeTab, setActiveTab] = useState("submit"); // "submit" or "history"

  const fetchClaims = React.useCallback(async () => {
    if (!currentUser?.uid) return;
    setLoadingClaims(true);
    setClaimsFetchError("");
    try {
      // Use only where() — no orderBy() — to avoid requiring a composite Firestore index.
      // Sort client-side instead.
      const q = query(
        collection(db, "reimbursements"),
        where("userId", "==", currentUser.uid)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by submittedAt descending (client-side)
      docs.sort((a, b) => {
        const ta = a.submittedAt?.toMillis?.() ?? 0;
        const tb = b.submittedAt?.toMillis?.() ?? 0;
        return tb - ta;
      });
      setMyClaims(docs);
    } catch (e) {
      console.error("Error fetching claims", e);
      setClaimsFetchError("Could not load claims. Please try again.");
    }
    setLoadingClaims(false);
  }, [currentUser]);

  React.useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const calcSub = rows => rows.reduce((s, r) => s + (parseFloat(r.amount || 0) || 0), 0);
  const tTotal = calcSub(transport);
  const aTotal = calcSub(accom);
  const mTotal = calcSub(meals);
  const oTotal = calcSub(other);
  const grandTotal = tTotal + aTotal + mTotal + oTotal - (parseFloat(advance) || 0);

  // Checked whenever the user tries to move past a section and again as a
  // safety net at final submit (the per-field cap below also prevents any
  // single row from exceeding this, so this is mainly a multi-row check).
  const validateSectionTotal = (rows, sectionName) => {
    const total = calcSub(rows);
    if (total > MAX_SECTION_TOTAL) {
      alert(`⚠️ Section Limit Exceeded!\n\n${sectionName} total is ₹${total.toFixed(2)}, which exceeds the maximum allowed limit of ₹${MAX_SECTION_TOTAL.toLocaleString("en-IN")} per section.\n\nPlease reduce the amounts, or submit the excess as a separate claim.`);
      return false;
    }
    return true;
  };

  const lastStep = STEPS.length - 1;
  const goNext = () => {
    if (currentStep === 1 && !agreed) {
      alert("Please accept the declaration before proceeding.");
      return;
    }
    if (currentStep === 2) {
      if (!emp.name || !emp.dept || !emp.email) {
        alert("Please fill in all required Employee fields (Name, Department, Email).");
        return;
      }
      if (!/^[A-Za-z\s\-']+$/.test(emp.name)) {
        alert("Employee Name can only contain letters, spaces, hyphens, or apostrophes.");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emp.email)) {
        alert("Please enter a valid Official Email.");
        return;
      }
      if (emp.phone && !/^\+?[0-9]{10,15}$/.test(emp.phone.replace(/[\s-]/g, ''))) {
        alert("Please enter a valid Mobile / Phone number (10-15 digits).");
        return;
      }
    }

    const validateRows = (rows, sectionName) => {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const hasData = Object.keys(row).some(k => k !== "files" && row[k] !== "" && row[k] !== null && row[k] !== undefined);
        if (!hasData) continue;

        // Check all required fields are filled when a row has any data
        const missing = [];
        if (sectionName === "Transportation") {
          if (!row.date) missing.push("Date");
          if (!row.mode) missing.push("Mode");
          if (!row.route || !row.route.trim()) missing.push("Route (From → To)");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Accommodation") {
          if (!row.checkin) missing.push("Check-in Date");
          if (!row.checkout) missing.push("Check-out Date");
          if (!row.hotel || !row.hotel.trim()) missing.push("Hotel / Property");
          if (!row.location || !row.location.trim()) missing.push("Location / City");
          if (!row.nights || parseFloat(row.nights) <= 0) missing.push("Nights (must be > 0)");
          if (!row.ratePerNight || parseFloat(row.ratePerNight) <= 0) missing.push("Per Night (must be > 0)");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Food & Meals") {
          if (!row.date) missing.push("Date");
          if (!row.desc || !row.desc.trim()) missing.push("Description / Vendor");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Other Incidentals") {
          if (!row.date) missing.push("Date");
          if (!row.desc || !row.desc.trim()) missing.push("Description");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        }
        if (missing.length > 0) {
          alert(`⚠️ Incomplete Row!\n\nRow ${i + 1} in ${sectionName} is partially filled.\n\nMissing required fields:\n• ${missing.join("\n• ")}\n\nPlease complete all fields before proceeding.`);
          return false;
        }

        // Cross-field date range checks
        if (sectionName === "Accommodation" && row.checkin && row.checkout && row.checkout < row.checkin) {
          alert(`❌ Invalid Dates in Row ${i + 1} (Accommodation)!\n\nCheck-out Date (${row.checkout}) cannot be before Check-in Date (${row.checkin}).\n\nPlease correct the dates.`);
          return false;
        }

        const amt = parseFloat(row.amount);
        if (!isNaN(amt) && amt < 0) {
          alert(`Invalid amount in Row ${i + 1} of ${sectionName}. Amounts cannot be negative.`);
          return false;
        }

        const textFields = [row.route, row.desc, row.hotel, row.location, row.remarks].filter(Boolean);
        for (const text of textFields) {
          if (/^[^a-zA-Z0-9]+$/.test(text)) {
            alert(`Please enter valid text in Row ${i + 1} of ${sectionName}. Nonsensical characters are not allowed.`);
            return false;
          }
        }

        // Proof / bill is not required for Own Vehicle transportation rows
        const proofOptional = sectionName === "Transportation" && row.mode === "Own Vehicle";
        if (!proofOptional && (!row.files || row.files.length === 0)) {
          alert(`⚠️ Proof / Bill Required!\n\nRow ${i + 1} in ${sectionName} has expense details entered but no supporting bill attached.\n\nPlease attach a bill for Row ${i + 1} before proceeding.`);
          return false;
        }
      }
      return true;
    };

    if (currentStep === 3 && !validateRows(transport, "Transportation")) return;
    if (currentStep === 3 && !validateSectionTotal(transport, "Transportation")) return;
    if (currentStep === 4 && !validateRows(accom, "Accommodation")) return;
    if (currentStep === 4 && !validateSectionTotal(accom, "Accommodation")) return;
    if (currentStep === 5 && !validateRows(meals, "Food & Meals")) return;
    if (currentStep === 5 && !validateSectionTotal(meals, "Food & Meals")) return;
    if (currentStep === 6 && !validateRows(other, "Other Incidentals")) return;
    if (currentStep === 6 && !validateSectionTotal(other, "Other Incidentals")) return;

    setCurrentStep(s => Math.min(s + 1, lastStep));
  };
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 0));

  // Guard stepper circle clicks with the same validation as goNext.
  // Jumping backward is always allowed; jumping forward requires all
  // intermediate sections to pass validation first.
  const handleStepClick = (targetStep) => {
    // Always allow going back or staying on same step
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    const validateRows = (rows, sectionName) => {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const hasData = Object.keys(row).some(k => k !== "files" && row[k] !== "" && row[k] !== null && row[k] !== undefined);
        if (!hasData) continue;

        // Check all required fields are filled when a row has any data
        const missing = [];
        if (sectionName === "Transportation") {
          if (!row.date) missing.push("Date");
          if (!row.mode) missing.push("Mode");
          if (!row.route || !row.route.trim()) missing.push("Route (From → To)");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Accommodation") {
          if (!row.checkin) missing.push("Check-in Date");
          if (!row.checkout) missing.push("Check-out Date");
          if (!row.hotel || !row.hotel.trim()) missing.push("Hotel / Property");
          if (!row.location || !row.location.trim()) missing.push("Location / City");
          if (!row.nights || parseFloat(row.nights) <= 0) missing.push("Nights (must be > 0)");
          if (!row.ratePerNight || parseFloat(row.ratePerNight) <= 0) missing.push("Per Night (must be > 0)");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Food & Meals") {
          if (!row.date) missing.push("Date");
          if (!row.desc || !row.desc.trim()) missing.push("Description / Vendor");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        } else if (sectionName === "Other Incidentals") {
          if (!row.date) missing.push("Date");
          if (!row.desc || !row.desc.trim()) missing.push("Description");
          if (!row.amount || parseFloat(row.amount) <= 0) missing.push("Amount (must be > 0)");
        }
        if (missing.length > 0) {
          alert(`⚠️ Incomplete Row!\n\nRow ${i + 1} in ${sectionName} is partially filled.\n\nMissing required fields:\n• ${missing.join("\n• ")}\n\nPlease complete all fields before proceeding.`);
          return false;
        }

        // Cross-field date range checks
        if (sectionName === "Accommodation" && row.checkin && row.checkout && row.checkout < row.checkin) {
          alert(`❌ Invalid Dates in Row ${i + 1} (Accommodation)!\n\nCheck-out Date (${row.checkout}) cannot be before Check-in Date (${row.checkin}).\n\nPlease correct the dates.`);
          return false;
        }

        const amt = parseFloat(row.amount);
        if (!isNaN(amt) && amt < 0) {
          alert(`Invalid amount in Row ${i + 1} of ${sectionName}. Amounts cannot be negative.`);
          return false;
        }
        const textFields = [row.route, row.desc, row.hotel, row.location, row.remarks].filter(Boolean);
        for (const text of textFields) {
          if (/^[^a-zA-Z0-9]+$/.test(text)) {
            alert(`Please enter valid text in Row ${i + 1} of ${sectionName}. Nonsensical characters are not allowed.`);
            return false;
          }
        }
        // Proof / bill is not required for Own Vehicle transportation rows
        const proofOptional = sectionName === "Transportation" && row.mode === "Own Vehicle";
        if (!proofOptional && (!row.files || row.files.length === 0)) {
          alert(`⚠️ Proof / Bill Required!\n\nRow ${i + 1} in ${sectionName} has expense details entered but no supporting bill attached.\n\nPlease attach a bill before navigating away.`);
          return false;
        }
      }
      return true;
    };

    // Validate each step from currentStep up to (but not including) targetStep
    for (let step = currentStep; step < targetStep; step++) {
      if (step === 1 && !agreed) {
        alert("Please accept the declaration before proceeding.");
        setCurrentStep(1);
        return;
      }
      if (step === 2) {
        if (!emp.name || !emp.dept || !emp.email) {
          alert("Please fill in all required Employee fields (Name, Department, Email).");
          setCurrentStep(2);
          return;
        }
        if (!/^[A-Za-z\s\-']+$/.test(emp.name)) {
          alert("Employee Name can only contain letters, spaces, hyphens, or apostrophes.");
          setCurrentStep(2);
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emp.email)) {
          alert("Please enter a valid Official Email.");
          setCurrentStep(2);
          return;
        }
        if (emp.phone && !/^\+?[0-9]{10,15}$/.test(emp.phone.replace(/[\s-]/g, ''))) {
          alert("Please enter a valid Mobile / Phone number (10-15 digits).");
          setCurrentStep(2);
          return;
        }
      }
      if (step === 3 && !validateRows(transport, "Transportation")) { setCurrentStep(3); return; }
      if (step === 3 && !validateSectionTotal(transport, "Transportation")) { setCurrentStep(3); return; }
      if (step === 4 && !validateRows(accom, "Accommodation")) { setCurrentStep(4); return; }
      if (step === 4 && !validateSectionTotal(accom, "Accommodation")) { setCurrentStep(4); return; }
      if (step === 5 && !validateRows(meals, "Food & Meals")) { setCurrentStep(5); return; }
      if (step === 5 && !validateSectionTotal(meals, "Food & Meals")) { setCurrentStep(5); return; }
      if (step === 6 && !validateRows(other, "Other Incidentals")) { setCurrentStep(6); return; }
      if (step === 6 && !validateSectionTotal(other, "Other Incidentals")) { setCurrentStep(6); return; }
    }

    setCurrentStep(targetStep);
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(',')[1];
      resolve({
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        base64: base64String
      });
    };
    reader.onerror = error => reject(error);
  });

  const handleSubmit = async () => {
    if (!agreed) { 
      alert("Please accept the declaration before submitting."); 
      setCurrentStep(1); 
      return; 
    }
    if (!emp.name || !emp.dept) {
      alert("Please fill in the Employee Name and Department.");
      setCurrentStep(2); 
      return;
    }

    // Guard against a "blank" claim: someone could fill in Employee Info,
    // accept the declaration, and click straight through to Submit without
    // ever entering an actual expense row (row-level validation only checks
    // rows that already contain data, so all-empty sections pass silently).
    // Block that here rather than accepting a ₹0.00 claim.
    const totalExpenses = tTotal + aTotal + mTotal + oTotal;
    if (totalExpenses <= 0) {
      alert("⚠️ No Expenses Entered!\n\nYour total claim amount is ₹0.00. Please enter at least one expense with an amount greater than ₹0 before submitting.");
      setCurrentStep(3);
      return;
    }

    const checkAllRowProofs = (rows, sectionName, stepIdx) => {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const hasData = Object.keys(row).some(k => k !== "files" && row[k] !== "" && row[k] !== null && row[k] !== undefined);
        // Proof / bill is not required for Own Vehicle transportation rows
        const proofOptional = sectionName === "Transportation" && row.mode === "Own Vehicle";
        if (hasData && !proofOptional && (!row.files || row.files.length === 0)) {
          alert(`⚠️ Proof / Bill Required!\n\nRow ${i + 1} in ${sectionName} has expense details entered but no supporting bill attached.\n\nPlease attach a bill for Row ${i + 1} before submitting.`);
          setCurrentStep(stepIdx);
          return false;
        }
      }
      return true;
    };

    if (!checkAllRowProofs(transport, "Transportation", 3)) return;
    if (!checkAllRowProofs(accom, "Accommodation", 4)) return;
    if (!checkAllRowProofs(meals, "Food & Meals", 5)) return;
    if (!checkAllRowProofs(other, "Other Incidentals", 6)) return;

    // Final safety-net check on the ₹1,00,000 per-section cap before submitting.
    if (!validateSectionTotal(transport, "Transportation")) { setCurrentStep(3); return; }
    if (!validateSectionTotal(accom, "Accommodation")) { setCurrentStep(4); return; }
    if (!validateSectionTotal(meals, "Food & Meals")) { setCurrentStep(5); return; }
    if (!validateSectionTotal(other, "Other Incidentals")) { setCurrentStep(6); return; }
    
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const collectRowFiles = (rows, category) => {
        const list = [];
        rows.forEach((r, idx) => {
          if (r.files && r.files.length > 0) {
            r.files.forEach(f => {
              list.push({
                file: f.file || f,
                category: `${category} (Row ${idx + 1})`,
                name: f.name
              });
            });
          }
        });
        return list;
      };

      const allFiles = [
        ...collectRowFiles(transport, "Transportation"),
        ...collectRowFiles(accom, "Accommodation"),
        ...collectRowFiles(meals, "Meals"),
        ...collectRowFiles(other, "Other")
      ];
      const base64Files = await Promise.all(allFiles.map(async f => {
        const b64 = await toBase64(f.file);
        b64.category = f.category;
        return b64;
      }));

      const payload = {
        token: API_TOKEN,
        emp,
        transport,
        accom,
        meals,
        other,
        totals: {
          transport: tTotal,
          accom: aTotal,
          meals: mTotal,
          other: oTotal,
          advance: parseFloat(advance) || 0,
          grandTotal
        },
        files: base64Files
      };

      const dateStr = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
      
      // Save data to Firestore for tracking and Finance approval
      await addDoc(collection(db, "reimbursements"), {
        userId: currentUser.uid,
        emp,
        transportTotal: tTotal,
        accomTotal: aTotal,
        mealsTotal: mTotal,
        otherTotal: oTotal,
        advance: parseFloat(advance) || 0,
        grandTotal,
        date: dateStr,
        status: "PENDING_FINANCE", // Waiting for Finance Department review
        submittedAt: serverTimestamp()
      });

      // Send data to the configured Google Apps Script Webhook
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload)
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        throw new Error("Invalid response from server. Please check the webhook URL.");
      }

      if (result.status === "error") {
        throw new Error(result.message || "Failed to submit.");
      }

      setSubmitted(true);
      fetchClaims(); // Refresh My Claims list after successful submission
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setSubmitError("There was an error submitting your claim: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    if (window.confirm("Clear all form data and start fresh?")) {
      setSubmitted(false);
      setCurrentStep(0);
      // Note: Employee Info (emp) is intentionally NOT reset here — it's read-only,
      // auto-filled from the user's profile, and won't repopulate itself after being
      // cleared (the sync effect only re-runs when userProfile/currentUser change).
      setTransport([emptyRow(transportCols), emptyRow(transportCols)]);
      setAccom([emptyRow(accomCols)]);
      setMeals([emptyRow(mealCols), emptyRow(mealCols)]);
      setOther([emptyRow(otherCols)]);
      setTransportFiles([]); setAccomFiles([]); setMealFiles([]); setOtherFiles([]);
      setAdvance(""); setAgreed(false);
      setIsSubmitting(false);
      setSubmitError("");
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setCurrentStep(3); // Skip policy, declaration, and employee info

    setTransport([emptyRow(transportCols), emptyRow(transportCols)]);
    setAccom([emptyRow(accomCols)]);
    setMeals([emptyRow(mealCols), emptyRow(mealCols)]);
    setOther([emptyRow(otherCols)]);
    setTransportFiles([]); setAccomFiles([]); setMealFiles([]); setOtherFiles([]);
    setAdvance(""); setAgreed(false);
    setIsSubmitting(false);
    setSubmitError("");
  };

  const renderStepBody = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={styles.policyBox}>
            {policies.map(p => (
              <div key={p.title} style={styles.policyItem}>
                <div style={styles.policyTitle}>📋 {p.title}</div>
                <div style={styles.policyText}>{p.text}</div>
              </div>
            ))}
          </div>
        );
      case 1:
        return (
          <>
            <p style={{ fontSize: 14, color: TEXT, lineHeight: 1.7, marginBottom: 16 }}>
              I hereby declare that the expenses listed in this form were incurred solely for official business purposes.
              All information provided is true, correct, and complete. Original invoices and receipts are attached as supporting documents.
              I understand that false, inflated, or fraudulent claims may result in disciplinary action and recovery of any amounts paid.
            </p>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <input
                id="declarationAgree"
                type="checkbox"
                style={{ width: 18, height: 18, accentColor: PRIMARY, cursor: "pointer", marginTop: 2 }}
                checked={agreed}
                onChange={() => setAgreed(!agreed)}
                aria-required="true"
              />
              <label htmlFor="declarationAgree" style={{ fontSize: 14, color: TEXT, cursor: "pointer" }}>
                <strong>I accept the above declaration</strong> and confirm that all details and receipts are accurate and attached.
                <span style={{ color: ERROR, marginLeft: 2 }} aria-hidden="true">*</span>
                <span style={styles.srOnly}>(required)</span>
              </label>
            </div>
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8, fontStyle: "italic" }}>
              Tip: press the Space bar anywhere on this page to check/uncheck the box above.
            </p>
            <p style={{ fontSize: 12, color: MUTED, marginTop: 16 }}>
              For queries contact: <strong>poojan@samriddhianveshana.com / sahana.uppala@samriddhianveshana.com</strong>
            </p>
          </>
        );
      case 2:
        return (
          <>
            <p style={{ fontSize: 13, color: MUTED, marginBottom: 12, fontStyle: "italic" }}>
              🔒 These details are auto-filled from your profile and cannot be edited here.
            </p>
            <div style={styles.row2}>
              <Field label="Employee Name" required>
                <div className="ent-input" style={{ ...styles.input, background: "#f5f5f5", color: "#444", cursor: "not-allowed", display: "flex", alignItems: "center" }}>
                  {emp.name || "—"}
                </div>
              </Field>
              <Field label="Department" required>
                <div className="ent-input" style={{ ...styles.input, background: "#f5f5f5", color: "#444", cursor: "not-allowed", display: "flex", alignItems: "center" }}>
                  {emp.dept || "—"}
                </div>
              </Field>
            </div>
            <div style={styles.row2}>
              <Field label="Official Email" required>
                <div className="ent-input" style={{ ...styles.input, background: "#f5f5f5", color: "#444", cursor: "not-allowed", display: "flex", alignItems: "center" }}>
                  {emp.email || "—"}
                </div>
              </Field>
              <Field label="Mobile / Phone">
                <div className="ent-input" style={{ ...styles.input, background: "#f5f5f5", color: "#444", cursor: "not-allowed", display: "flex", alignItems: "center" }}>
                  {emp.phone || "—"}
                </div>
              </Field>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <ExpenseTable columns={transportCols} rows={transport} onChange={setTransport} />
            <SectionLimitNote total={tTotal} limit={MAX_SECTION_TOTAL} />
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
              📌 Own vehicle: attach odometer reading if available. Bill/proof attachment is optional for Own Vehicle trips. Mileage reimbursed at the approved rate per km.
            </p>
          </>
        );
      case 4:
        return (
          <>
            <ExpenseTable columns={accomCols} rows={accom} onChange={setAccom} />
            <SectionLimitNote total={aTotal} limit={MAX_SECTION_TOTAL} />
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
              📌 Policy limit: refer to Travel Policy Annexure A for grade-wise hotel limits.
            </p>
          </>
        );
      case 5:
        return (
          <>
            <ExpenseTable columns={mealCols} rows={meals} onChange={setMeals} />
            <SectionLimitNote total={mTotal} limit={MAX_SECTION_TOTAL} />
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
              ⚠️ Alcohol, personal items, and minibar charges are <strong>NOT</strong> reimbursable.
            </p>
          </>
        );
      case 6:
        return (
          <>
            <ExpenseTable columns={otherCols} rows={other} onChange={setOther} />
            <SectionLimitNote total={oTotal} limit={MAX_SECTION_TOTAL} />
          </>
        );
      case 7:
        return (
          <table style={styles.summaryTable}>
            <thead>
              <tr>
                <th style={styles.sTh}>Category</th>
                <th style={{ ...styles.sTh, textAlign: "right" }}>Sub-Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Transportation (Section 4)", tTotal],
                ["Accommodation (Section 5)", aTotal],
                ["Food & Meals (Section 6)", mTotal],
                ["Other Incidentals (Section 7)", oTotal],
              ].map(([label, val]) => (
                <tr key={label} style={styles.sTr}>
                  <td style={styles.sTd}>{label}</td>
                  <td style={styles.sTdRight}>₹ {val.toFixed(2)}</td>
                </tr>
              ))}
              <tr style={styles.sTr}>
                <td style={styles.sTd}>
                  Less: Advance Paid
                  <input type="number" value={advance} onChange={e => setAdvance(e.target.value.replace(/-/g, ''))}
                    onKeyDown={e => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                    placeholder="Enter advance amount" className="ent-input"
                    style={{ ...styles.tInput, maxWidth: 180, marginLeft: 12, fontSize: 13 }} />
                </td>
                <td style={styles.sTdRight}>- ₹ {(parseFloat(advance) || 0).toFixed(2)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={styles.sTrTotal}>
                <td style={{ ...styles.sTd, fontWeight: 700, color: PRIMARY }}>TOTAL CLAIM AMOUNT</td>
                <td style={{ ...styles.sTdRight, fontWeight: 700, color: PRIMARY, fontSize: 16 }}>₹ {grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        );
      default:
        return null;
    }
  };

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div style={styles.page}>
        <DesignSystemStyles />
        <div style={styles.formWrap}>

        {/* HEADER */}
        <div style={styles.headerCard}>
          <div style={styles.headerTop}>
            <div>
              <h1 style={styles.formTitle}>Travel & Expense Reimbursement</h1>
              <p style={styles.formDesc}>
                Use this form to claim reimbursement for travel, accommodation, and meal expenses incurred for official business.
                Attach all original invoices before submitting to the Finance department.
              </p>
            </div>
            <button className="ent-btn-ghost" style={styles.ghostBtn} onClick={handleClear}>Clear form</button>
          </div>
          <p style={styles.requiredNote}>* Indicates required question</p>
        </div>

        {/* TAB SWITCHER */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: `2px solid ${BORDER}` }}>
          {[
            { key: 'submit', label: 'Submit Claim' },
            { key: 'history', label: 'My Claims' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.key ? `2px solid ${PRIMARY}` : '2px solid transparent',
                marginBottom: -2,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? PRIMARY : MUTED,
                cursor: 'pointer',
                transition: 'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = TEXT; }}
              onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = MUTED; }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'history' ? (
          <TravelReimbursementHistory claims={myClaims} loading={loadingClaims} error={claimsFetchError} onRefresh={fetchClaims} />
        ) : !isSubmissionAllowed ? (
          <div style={{ padding: "40px 32px", backgroundColor: "#fff3f3", border: "1px solid #ffcdd2", borderRadius: "12px", textAlign: "center", marginTop: "24px" }}>
            <div style={{ fontSize: "56px", marginBottom: "20px" }}>🔒</div>
            <h3 style={{ color: "#d32f2f", fontSize: "24px", marginBottom: "16px", fontWeight: "700" }}>Submissions Closed</h3>
            <p style={{ color: "#5f6368", fontSize: "16px", lineHeight: "1.7", maxWidth: "600px", margin: "0 auto" }}>
              The Travel Reimbursement window is currently closed. Claims can only be submitted between the <strong>1st and the 25th</strong> of every month. Please hold on to your receipts and submit your claims when the window reopens next month.
            </p>
          </div>
        ) : submitted ? (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Claim Submitted Successfully</h2>
            <p style={styles.successDesc}>
              Your reimbursement claim has been forwarded to the Finance department for review.
              You will receive an email notification once it has been processed.
            </p>
            <button className="ent-btn-primary" style={styles.primaryBtn} onClick={handleSubmitAnother}>Submit Another Claim</button>
          </div>
        ) : (
          <>
            {/* STEPPER */}
            <div style={styles.stepperCard}>
              <Stepper steps={STEPS} currentStep={currentStep} maxStepReached={maxStepReached} onStepClick={handleStepClick} />
            </div>

            {/* ACTIVE STEP */}
            <div style={styles.sectionCard}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>{STEPS[currentStep].title}</h2>
                <p style={styles.sectionSub}>{STEPS[currentStep].sub}</p>
              </div>
              <div style={styles.sectionBody}>
                {renderStepBody()}

                <div style={styles.navRow}>
                  {currentStep > 0 ? (
                    <button className="ent-btn-secondary" style={styles.secondaryBtn} onClick={goBack}>← Back</button>
                  ) : <span />}
                  <div style={styles.navSpacer} />
                  {currentStep < lastStep ? (
                    <button className="ent-btn-primary" style={styles.primaryBtn} onClick={goNext}>Next →</button>
                  ) : (
                    <button 
                      className="ent-btn-primary" 
                      style={{...styles.primaryBtn, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? "not-allowed" : "pointer"}} 
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Claim →"}
                    </button>
                  )}
                </div>
                {submitError && (
                  <div style={{ marginTop: 16, padding: 12, borderRadius: 6, background: "#FEE2E2", color: ERROR, fontSize: 14 }}>
                    {submitError}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </Layout>
  );
}